// AI-Powered Mood Board Generator
import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Palette,
  Sparkles,
  Heart,
  Sun,
  Moon,
  Leaf,
  Snowflake,
  Flower2,
  Star,
  Crown,
  Coffee,
  Wine,
  Cake,
  Music,
  Mountain,
  Waves,
  TreePine,
  Building2,
  Home,
  PartyPopper,
  Gift,
  Users,
  Loader2,
  CheckCircle2,
  ShoppingBag,
  ChefHat
} from 'lucide-react';
import { products } from '../../lib/data';
import { suppliers } from '../../lib/suppliers';
import { useToast } from '../../hooks/toast';

const aesthetics = [
  { id: 'minimalist', name: 'Minimalist', icon: Building2 },
  { id: 'bohemian', name: 'Bohemian', icon: Flower2 },
  { id: 'elegant', name: 'Elegant', icon: Crown },
  { id: 'rustic', name: 'Rustic', icon: TreePine },
  { id: 'modern', name: 'Modern', icon: Star },
  { id: 'vintage', name: 'Vintage', icon: Coffee },
  { id: 'romantic', name: 'Romantic', icon: Heart },
  { id: 'natural', name: 'Natural', icon: Leaf }
];

const colors = [
  { id: 'pastels', name: 'Pastels', colors: ['#FFB3BA', '#FFDFBA', '#FFFFBA', '#BAFFC9', '#BAE1FF'] },
  { id: 'jewel', name: 'Jewel Tones', colors: ['#9B59B6', '#E74C3C', '#27AE60', '#F39C12', '#3498DB'] },
  { id: 'earth', name: 'Earth Tones', colors: ['#8B4513', '#D2691E', '#CD853F', '#DEB887', '#F5DEB3'] },
  { id: 'monochrome', name: 'Monochrome', colors: ['#FFFFFF', '#E5E5E5', '#CCCCCC', '#666666', '#000000'] },
  { id: 'sunset', name: 'Sunset', colors: ['#FF6B6B', '#FFB347', '#FFDC80', '#FFE5B4', '#FFF5E1'] },
  { id: 'ocean', name: 'Ocean', colors: ['#006994', '#1E88E5', '#42A5F5', '#64B5F6', '#90CAF9'] },
  { id: 'garden', name: 'Garden', colors: ['#4CAF50', '#8BC34A', '#CDDC39', '#FFC107', '#FF9800'] },
  { id: 'royal', name: 'Royal', colors: ['#673AB7', '#9C27B0', '#D4AF37', '#FFD700', '#FFF8DC'] }
];

const eventTypes = [
  { id: 'wedding', name: 'Wedding', icon: Heart },
  { id: 'birthday', name: 'Birthday', icon: Cake },
  { id: 'corporate', name: 'Corporate', icon: Building2 },
  { id: 'garden-party', name: 'Garden Party', icon: Flower2 },
  { id: 'dinner', name: 'Dinner Party', icon: Wine },
  { id: 'baby-shower', name: 'Baby Shower', icon: Gift },
  { id: 'anniversary', name: 'Anniversary', icon: PartyPopper },
  { id: 'gathering', name: 'Social Gathering', icon: Users }
];

const moods = [
  { id: 'romantic', name: 'Romantic', icon: Heart },
  { id: 'energetic', name: 'Energetic', icon: Sparkles },
  { id: 'calm', name: 'Calm', icon: Waves },
  { id: 'festive', name: 'Festive', icon: PartyPopper },
  { id: 'sophisticated', name: 'Sophisticated', icon: Wine },
  { id: 'cozy', name: 'Cozy', icon: Home },
  { id: 'vibrant', name: 'Vibrant', icon: Music },
  { id: 'serene', name: 'Serene', icon: Mountain }
];

const seasons = [
  { id: 'spring', name: 'Spring', icon: Flower2 },
  { id: 'summer', name: 'Summer', icon: Sun },
  { id: 'fall', name: 'Fall', icon: Leaf },
  { id: 'winter', name: 'Winter', icon: Snowflake }
];

export default function MoodBoardPage() {
  const [selectedAesthetics, setSelectedAesthetics] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedEventType, setSelectedEventType] = useState(null);
  const [selectedMoods, setSelectedMoods] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generatedTheme, setGeneratedTheme] = useState(null);
  const { toast } = useToast();

  const toggleSelection = (item, selectedList, setSelectedList) => {
    if (selectedList.includes(item)) {
      setSelectedList(selectedList.filter(i => i !== item));
    } else {
      setSelectedList([...selectedList, item]);
    }
  };

  const generateTheme = async () => {
    if (!selectedEventType) {
      toast({
        title: "Event type required",
        description: "Please select an event type to generate your theme.",
        variant: "destructive"
      });
      return;
    }

    setGenerating(true);

    try {
      // Generate theme based on selections
      const theme = await generateAITheme({
        aesthetics: selectedAesthetics,
        colors: selectedColors,
        eventType: selectedEventType,
        moods: selectedMoods,
        season: selectedSeason,
        availableProducts: products,
        availableSuppliers: suppliers
      });

      setGeneratedTheme(theme);

      toast({
        title: "Theme generated!",
        description: "Your personalized event theme is ready.",
      });
    } catch (error) {
      console.error('Error generating theme:', error);
      toast({
        title: "Generation failed",
        description: "Please try again or adjust your selections.",
        variant: "destructive"
      });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center animate-pulse">
              <Sparkles className="h-8 w-8 text-primary-foreground" />
            </div>
          </div>
          <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
            AI Event Designer
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Tell us your vision, and our AI will create a complete event theme with products, food, and setup ideas tailored to what we actually offer
          </p>
        </div>

        {!generatedTheme ? (
          <SelectionInterface
            aesthetics={aesthetics}
            colors={colors}
            eventTypes={eventTypes}
            moods={moods}
            seasons={seasons}
            selectedAesthetics={selectedAesthetics}
            setSelectedAesthetics={setSelectedAesthetics}
            selectedColors={selectedColors}
            setSelectedColors={setSelectedColors}
            selectedEventType={selectedEventType}
            setSelectedEventType={setSelectedEventType}
            selectedMoods={selectedMoods}
            setSelectedMoods={setSelectedMoods}
            selectedSeason={selectedSeason}
            setSelectedSeason={setSelectedSeason}
            toggleSelection={toggleSelection}
            generating={generating}
            generateTheme={generateTheme}
          />
        ) : (
          <GeneratedThemeDisplay
            theme={generatedTheme}
            onStartOver={() => setGeneratedTheme(null)}
          />
        )}
      </div>
    </div>
  );
}

// Selection Interface Component
function SelectionInterface({
  aesthetics,
  colors,
  eventTypes,
  moods,
  seasons,
  selectedAesthetics,
  setSelectedAesthetics,
  selectedColors,
  setSelectedColors,
  selectedEventType,
  setSelectedEventType,
  selectedMoods,
  setSelectedMoods,
  selectedSeason,
  setSelectedSeason,
  toggleSelection,
  generating,
  generateTheme
}) {
  return (
    <div className="space-y-8">
      {/* Event Type Selection */}
      <Card className="backdrop-blur-sm bg-card/70 border-2 border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5" />
            Event Type <span className="text-red-500">*</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {eventTypes.map(type => {
              const Icon = type.icon;
              const isSelected = selectedEventType === type.id;
              return (
                <button
                  key={type.id}
                  onClick={() => setSelectedEventType(type.id)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    isSelected
                      ? 'border-primary bg-secondary/60 scale-105'
                      : 'border-border hover:border-primary/40'
                  }`}
                >
                  <Icon className={`h-8 w-8 mx-auto mb-2 ${
                    isSelected ? 'text-primary' : 'text-muted-foreground'
                  }`} />
                  <p className={`text-sm font-semibold ${
                    isSelected ? 'text-primary' : 'text-foreground'
                  }`}>
                    {type.name}
                  </p>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Aesthetic Selection */}
      <Card className="backdrop-blur-sm bg-card/70 border-2 border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Aesthetic Preferences
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">Select multiple aesthetics that resonate with you</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {aesthetics.map(aesthetic => {
              const Icon = aesthetic.icon;
              const isSelected = selectedAesthetics.includes(aesthetic.id);
              return (
                <button
                  key={aesthetic.id}
                  onClick={() => toggleSelection(aesthetic.id, selectedAesthetics, setSelectedAesthetics)}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    isSelected
                      ? 'border-primary bg-secondary/60'
                      : 'border-border hover:border-primary/40'
                  }`}
                >
                  <Icon className={`h-6 w-6 mx-auto mb-1 ${
                    isSelected ? 'text-primary' : 'text-muted-foreground'
                  }`} />
                  <p className="text-xs font-medium">{aesthetic.name}</p>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Color Scheme Selection */}
      <Card className="backdrop-blur-sm bg-card/70 border-2 border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Color Schemes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">Choose color palettes you love</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {colors.map(colorScheme => {
              const isSelected = selectedColors.includes(colorScheme.id);
              return (
                <button
                  key={colorScheme.id}
                  onClick={() => toggleSelection(colorScheme.id, selectedColors, setSelectedColors)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    isSelected
                      ? 'border-primary bg-secondary/60'
                      : 'border-border hover:border-primary/40'
                  }`}
                >
                  <div className="flex gap-1 mb-2 h-6">
                    {colorScheme.colors.map((color, idx) => (
                      <div
                        key={idx}
                        className="flex-1 rounded"
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                  <p className="text-xs font-medium">{colorScheme.name}</p>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Mood Selection */}
      <Card className="backdrop-blur-sm bg-card/70 border-2 border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5" />
            Event Mood & Vibe
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">What atmosphere do you want to create?</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {moods.map(mood => {
              const Icon = mood.icon;
              const isSelected = selectedMoods.includes(mood.id);
              return (
                <button
                  key={mood.id}
                  onClick={() => toggleSelection(mood.id, selectedMoods, setSelectedMoods)}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    isSelected
                      ? 'border-primary bg-secondary/60'
                      : 'border-border hover:border-primary/40'
                  }`}
                >
                  <Icon className={`h-6 w-6 mx-auto mb-1 ${
                    isSelected ? 'text-primary' : 'text-muted-foreground'
                  }`} />
                  <p className="text-xs font-medium">{mood.name}</p>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Season Selection */}
      <Card className="backdrop-blur-sm bg-card/70 border-2 border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sun className="h-5 w-5" />
            Season
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {seasons.map(season => {
              const Icon = season.icon;
              const isSelected = selectedSeason === season.id;
              return (
                <button
                  key={season.id}
                  onClick={() => setSelectedSeason(season.id)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    isSelected
                      ? 'border-primary bg-secondary/60'
                      : 'border-border hover:border-primary/40'
                  }`}
                >
                  <Icon className={`h-8 w-8 mx-auto mb-2 ${
                    isSelected ? 'text-primary' : 'text-muted-foreground'
                  }`} />
                  <p className={`text-sm font-semibold ${
                    isSelected ? 'text-primary' : 'text-foreground'
                  }`}>
                    {season.name}
                  </p>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Generate Button */}
      <div className="flex justify-center">
        <Button
          onClick={generateTheme}
          disabled={generating || !selectedEventType}
          size="lg"
          className="bg-primary text-primary-foreground hover:bg-primary/90 px-12 py-6 text-lg"
        >
          {generating ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Generating Your Perfect Theme...
            </>
          ) : (
            <>
              <Sparkles className="mr-2 h-5 w-5" />
              Generate My Event Theme
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

// AI Theme Generation Function
async function generateAITheme(selections) {
  const {
    aesthetics,
    colors,
    eventType,
    moods,
    season,
    availableProducts,
    availableSuppliers
  } = selections;

  // Simulate AI processing (2 seconds)
  await new Promise(resolve => setTimeout(resolve, 2000));

  const theme = {
    name: generateThemeName(eventType, aesthetics, season),
    description: generateThemeDescription(eventType, aesthetics, moods, season),
    colorPalette: selectColorPalette(colors),
    products: selectRelevantProducts(availableProducts, aesthetics, colors, eventType, season),
    food: generateFoodRecommendations(eventType, moods, season),
    setup: generateSetupIdeas(eventType, aesthetics, moods),
    suppliers: selectRelevantSuppliers(availableSuppliers, eventType)
  };

  return theme;
}

function generateThemeName(eventType, aesthetics, season) {
  const typeNames = {
    wedding: 'Wedding',
    birthday: 'Celebration',
    corporate: 'Corporate Event',
    'garden-party': 'Garden Soirée',
    dinner: 'Dinner Party',
    'baby-shower': 'Baby Shower',
    anniversary: 'Anniversary',
    gathering: 'Gathering'
  };

  const aestheticNames = {
    minimalist: 'Minimalist Chic',
    bohemian: 'Bohemian Dreams',
    elegant: 'Timeless Elegance',
    rustic: 'Rustic Charm',
    modern: 'Modern Luxe',
    vintage: 'Vintage Romance',
    romantic: 'Romantic Bliss',
    natural: 'Natural Beauty'
  };

  const seasonNames = {
    spring: 'Spring Bloom',
    summer: 'Summer Radiance',
    fall: 'Autumn Warmth',
    winter: 'Winter Wonderland'
  };

  const parts = [];
  if (season && seasonNames[season]) parts.push(seasonNames[season]);
  if (aesthetics.length > 0 && aestheticNames[aesthetics[0]]) parts.push(aestheticNames[aesthetics[0]]);
  parts.push(typeNames[eventType] || 'Event');

  return parts.join(' ');
}

function generateThemeDescription(eventType, aesthetics, moods, season) {
  const descriptions = {
    wedding: 'A beautiful celebration of love featuring carefully curated elements that create an unforgettable atmosphere.',
    birthday: 'A joyful celebration designed to create lasting memories with personalized touches throughout.',
    corporate: 'A sophisticated professional gathering that balances elegance with engaging elements.',
    'garden-party': 'An outdoor celebration that brings together natural beauty and refined touches.',
    dinner: 'An intimate gathering featuring exquisite culinary experiences and elegant ambiance.',
    'baby-shower': 'A sweet celebration honoring new beginnings with thoughtful, charming details.',
    anniversary: 'A meaningful celebration of enduring love with romantic and personalized elements.',
    gathering: 'A warm social event designed to bring people together in style and comfort.'
  };

  let description = descriptions[eventType] || 'A beautiful event crafted with care and attention to detail.';

  if (aesthetics.includes('minimalist')) {
    description += ' Clean lines and uncluttered spaces create a sense of calm sophistication.';
  } else if (aesthetics.includes('bohemian')) {
    description += ' Eclectic and free-spirited elements combine to create a relaxed, artistic atmosphere.';
  } else if (aesthetics.includes('elegant')) {
    description += ' Refined details and luxurious touches elevate every aspect of the experience.';
  }

  if (season === 'spring') {
    description += ' Fresh blooms and light colors capture the essence of renewal and growth.';
  } else if (season === 'summer') {
    description += ' Bright, vibrant elements reflect the warmth and energy of the season.';
  } else if (season === 'fall') {
    description += ' Rich, warm tones and natural textures create a cozy, inviting atmosphere.';
  } else if (season === 'winter') {
    description += ' Crisp elegance and warm accents create a magical seasonal experience.';
  }

  return description;
}

function selectColorPalette(selectedColorIds) {
  const colorSchemes = {
    pastels: ['#FFB3BA', '#FFDFBA', '#FFFFBA', '#BAFFC9', '#BAE1FF'],
    jewel: ['#9B59B6', '#E74C3C', '#27AE60', '#F39C12', '#3498DB'],
    earth: ['#8B4513', '#D2691E', '#CD853F', '#DEB887', '#F5DEB3'],
    monochrome: ['#FFFFFF', '#E5E5E5', '#CCCCCC', '#666666', '#000000'],
    sunset: ['#FF6B6B', '#FFB347', '#FFDC80', '#FFE5B4', '#FFF5E1'],
    ocean: ['#006994', '#1E88E5', '#42A5F5', '#64B5F6', '#90CAF9'],
    garden: ['#4CAF50', '#8BC34A', '#CDDC39', '#FFC107', '#FF9800'],
    royal: ['#673AB7', '#9C27B0', '#D4AF37', '#FFD700', '#FFF8DC']
  };

  if (selectedColorIds.length > 0) {
    return colorSchemes[selectedColorIds[0]] || colorSchemes.pastels;
  }
  return colorSchemes.pastels;
}

function selectRelevantProducts(products, aesthetics, colors, eventType, season) {
  let relevantProducts = [...products];

  if (eventType === 'garden-party' || eventType === 'wedding') {
    relevantProducts = relevantProducts.filter(p =>
      p.category === 'Flowers' || p.category === 'Candles' || p.category === 'Ceramic & Pottery'
    );
  }

  if (eventType === 'dinner' || aesthetics.includes('romantic')) {
    relevantProducts = relevantProducts.filter(p =>
      p.category === 'Candles' || p.category === 'Soaps' || p.category === 'Flowers'
    );
  }

  if (season === 'spring') {
    relevantProducts = relevantProducts.filter(p =>
      p.name.toLowerCase().includes('rose') ||
      p.name.toLowerCase().includes('lavender') ||
      p.name.toLowerCase().includes('pastel') ||
      p.category === 'Flowers'
    );
  } else if (season === 'winter') {
    relevantProducts = relevantProducts.filter(p =>
      p.name.toLowerCase().includes('vanilla') ||
      p.name.toLowerCase().includes('amber') ||
      p.name.toLowerCase().includes('cedarwood') ||
      p.category === 'Candles'
    );
  }

  return relevantProducts.slice(0, 8);
}

function generateFoodRecommendations(eventType, moods, season) {
  const recommendations = [];

  if (eventType === 'wedding' || eventType === 'anniversary') {
    recommendations.push({
      category: 'Sweet Delights',
      items: [
        { name: 'Multi-tiered Custom Cake', description: 'Elegant cake with your choice of flavors and decorations from Sweet Delights Patisserie' },
        { name: 'Artisanal Macarons Tower', description: 'Colorful macarons in various flavors, beautifully displayed' },
        { name: 'Choux & Éclairs Selection', description: 'Classic French pastries with gourmet fillings' }
      ]
    });
    recommendations.push({
      category: 'Savory Selections',
      items: [
        { name: 'Alps Cheese & Charcuterie Board', description: 'Premium selection from Alpenkäserei Allgäu and local Bayern farmers' },
        { name: 'Gourmet Canapés', description: 'Bite-sized savory appetizers with seasonal ingredients from Käfer Feinkost' },
        { name: 'Organic Herb-Roasted Vegetables', description: 'Fresh vegetables from Isarland Ökokiste' }
      ]
    });
  } else if (eventType === 'birthday' || eventType === 'baby-shower') {
    recommendations.push({
      category: 'Sweet Treats',
      items: [
        { name: 'Gourmet Cupcakes', description: 'Beautifully decorated cupcakes with buttercream and ganache options' },
        { name: 'Classic Madeleines', description: 'Soft, buttery French cakes perfect with tea or coffee' },
        { name: 'Fresh Fruit Tarts', description: 'Seasonal fruits on buttery pastry base' }
      ]
    });
  } else if (eventType === 'dinner' || eventType === 'corporate') {
    recommendations.push({
      category: 'Premium Catering',
      items: [
        { name: 'Käfer Feinkost Selection', description: 'Gourmet appetizers and main courses from Munich premium caterer (€35-55/person)' },
        { name: 'Traditional Bavarian Buffet', description: 'Authentic local cuisine from Königlicher Hirschgarten (€25-40/person)' },
        { name: 'Artisan Bread & Butter', description: 'Fresh baked goods from Manufactum Brot & Butter' }
      ]
    });
  } else if (eventType === 'garden-party') {
    recommendations.push({
      category: 'Garden Party Fare',
      items: [
        { name: 'Organic Salad Bar', description: 'Fresh vegetables from Isarland Ökokiste delivered weekly' },
        { name: 'Premium Grilled Selection', description: 'Organic meats and sausages from Herrmannsdorfer Landwerkstätten' },
        { name: 'Viennoiserie Assortment', description: 'Fresh French pastries for afternoon tea service' }
      ]
    });
  }

  return recommendations;
}

function generateSetupIdeas(eventType, aesthetics, moods) {
  const ideas = [];

  if (aesthetics.includes('minimalist')) {
    ideas.push({
      area: 'Table Setting',
      description: 'Clean, uncluttered tables with simple ceramic pieces as centerpieces. Use monochrome color palette with subtle gold accents from our ceramic collection.',
      products: ['Minimalist Ceramic Holders', 'Ocean Breeze Candle']
    });
  } else if (aesthetics.includes('bohemian')) {
    ideas.push({
      area: 'Table Setting',
      description: 'Eclectic mix of textures with dried flowers, vintage-inspired candle holders, and artisan soaps as decorative favors for guests.',
      products: ['Dried Lavender Bunch', 'Decorative Birch Twigs', 'Lavender Dream Soap']
    });
  } else if (aesthetics.includes('elegant') || aesthetics.includes('romantic')) {
    ideas.push({
      area: 'Table Setting',
      description: 'Luxurious table arrangements with fresh rose bouquets, gilded agate coasters, and scented candles creating intimate ambiance.',
      products: ['Pastel Rose Bouquet', 'Gilded Agate Coasters', 'Vanilla Bean Candle']
    });
  }

  if (eventType === 'garden-party') {
    ideas.push({
      area: 'Outdoor Space',
      description: 'Natural arrangements with wildflowers, small potted plants, and rustic wooden elements. String lights for evening ambiance (available from Munich suppliers).',
      products: ['Rustic Wildflower Mix', 'Mini Fiddle Leaf Fig', 'Decorative Birch Twigs']
    });
  }

  ideas.push({
    area: 'Guest Favors',
    description: 'Personalized gift boxes with handmade soaps and small candles, wrapped in premium packaging from Packari or AliExpress suppliers with custom labels from Etikett.de.',
    products: ['Lavender Dream Soap', 'Ocean Breeze Candle']
  });

  ideas.push({
    area: 'Fragrance & Atmosphere',
    description: 'Carefully selected scents throughout the space using our candles and essential oils from verified Amazon.de suppliers (Mystic Moments, Naissance brands - NO AliExpress oils, they are often fake!).',
    products: ['Ocean Breeze Candle', 'Vanilla Bean Candle', 'Lavender Dream Soap']
  });

  return ideas;
}

function selectRelevantSuppliers(suppliers, eventType) {
  const relevantCategories = ['farmers', 'candles', 'fragrances', 'packaging', 'flowers', 'catering'];
  return suppliers
    .filter(s => relevantCategories.includes(s.category))
    .slice(0, 5);
}

// Generated Theme Display Component
function GeneratedThemeDisplay({ theme, onStartOver }) {
  return (
    <div className="space-y-8 animate-fade-in">
      <div className="text-center">
        <div className="flex items-center justify-center mb-4">
          <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center">
            <CheckCircle2 className="h-8 w-8 text-primary-foreground" />
          </div>
        </div>
        <h2 className="text-4xl font-bold mb-3 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
          {theme.name}
        </h2>
        <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
          {theme.description}
        </p>
      </div>

      <Card className="backdrop-blur-sm bg-card/70 border-2 border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Color Palette
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 justify-center flex-wrap">
            {theme.colorPalette.map((color, idx) => (
              <div key={idx} className="text-center">
                <div
                  className="w-20 h-20 rounded-lg shadow-lg mb-2"
                  style={{ backgroundColor: color }}
                />
                <p className="text-xs font-mono text-muted-foreground">{color}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="backdrop-blur-sm bg-card/70 border-2 border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingBag className="h-5 w-5" />
            Recommended Products from Our Collection
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {theme.products.map(product => (
              <div
                key={product.id}
                className="p-4 rounded-lg border-2 border-border hover:border-primary/40 transition-all hover:shadow-lg cursor-pointer"
                onClick={() => window.location.hash = '#/products'}
              >
                <Badge className="mb-2">{product.category}</Badge>
                <h4 className="font-semibold mb-1">{product.name}</h4>
                <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                  {product.description}
                </p>
                <p className="text-lg font-bold text-primary">€{product.price.toFixed(2)}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="backdrop-blur-sm bg-card/70 border-2 border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ChefHat className="h-5 w-5" />
            Food & Catering Options
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {theme.food.map((category, idx) => (
              <div key={idx}>
                <h4 className="font-bold text-lg mb-3 text-primary">
                  {category.category}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {category.items.map((item, itemIdx) => (
                    <div
                      key={itemIdx}
                      className="p-4 rounded-lg bg-muted/40 border border-border"
                    >
                      <h5 className="font-semibold mb-1">{item.name}</h5>
                      <p className="text-sm text-muted-foreground">
                        {item.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="backdrop-blur-sm bg-card/70 border-2 border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Home className="h-5 w-5" />
            Setup & Decoration Ideas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {theme.setup.map((idea, idx) => (
              <div
                key={idx}
                className="p-4 rounded-lg bg-muted/40 border border-border"
              >
                <h4 className="font-bold text-lg mb-2 text-primary">
                  {idea.area}
                </h4>
                <p className="text-muted-foreground mb-3">
                  {idea.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {idea.products.map((product, pIdx) => (
                    <Badge key={pIdx} variant="outline" className="bg-background">
                      {product}
                    </Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-center gap-4 flex-wrap">
        <Button
          onClick={onStartOver}
          variant="outline"
          size="lg"
          className="px-8"
        >
          Create Another Theme
        </Button>
        <Button
          onClick={() => window.location.hash = '#/products'}
          size="lg"
          className="bg-primary text-primary-foreground hover:bg-primary/90 px-8"
        >
          <ShoppingBag className="mr-2 h-4 w-4" />
          Shop Products
        </Button>
        <Button
          onClick={() => window.location.hash = '#/booking'}
          size="lg"
          className="bg-secondary text-secondary-foreground hover:bg-secondary/80 px-8"
        >
          <Gift className="mr-2 h-4 w-4" />
          Book Event
        </Button>
      </div>
    </div>
  );
}
